import { isEmpty } from "lodash"
import * as API from "../api"
import {
    EmployeeFormStore,
    EmployeeWorkShiftStartStopTime,
    SignatureRowFields,
    SignatureState,
    Thunk,
    TimekeepingEntry,
    TKSignTimecardModalTarget,
    tRequestTimekeepingSignatures,
} from "../common/types"
import { getFlagEnabled } from "../getFlagValue"
import { tContext } from "../components/custom-dashboards/types"
import { openTKSignTimecardModal } from "../components/modals/actions"
import { setNotificationMessage } from "../websockets/actions"
import { iEmployee } from "../cached-data/types"
import { sourceDataSelector } from "../selectors/custom-dashboard"
import { sourceDataUpdated } from "../dashboard-data/actions/dashboard-data"

export const REQUEST_TIMEKEEPING_SIGNATURES_SUCCESS = "REQUEST_TIMEKEEPING_SIGNATURES_SUCCESS"
export const REQUEST_TIMEKEEPING_SIGNATURES_FAILED = "REQUEST_TIMEKEEPING_SIGNATURES_FAILED"

export interface iRequestTimekeepingSignaturesSuccess {
    type: typeof REQUEST_TIMEKEEPING_SIGNATURES_SUCCESS
    payload: { url: string }
}

export interface iRequestTimekeepingSignaturesFailed {
    type: typeof REQUEST_TIMEKEEPING_SIGNATURES_FAILED
    payload: { error: Record<string, any> }
}

export type requestTimekeepingSignaturesActionTypes = iRequestTimekeepingSignaturesSuccess | iRequestTimekeepingSignaturesFailed

export type requestTimekeepingSignaturesActionState = tRequestTimekeepingSignatures

export const requestTimekeepingSignaturesSuccess = (payload: { url: string }): iRequestTimekeepingSignaturesSuccess => {
    return {
        type: REQUEST_TIMEKEEPING_SIGNATURES_SUCCESS,
        payload: payload,
    }
}

export const requestTimekeepingSignaturesFailed = (error: Record<string, any>): iRequestTimekeepingSignaturesFailed => {
    return {
        type: REQUEST_TIMEKEEPING_SIGNATURES_FAILED,
        payload: { error: error },
    }
}

/**
 * Can be used to request multiple signatures via text. Will post notification based on successes/errors after all
 * text requests have been sent.
 * @param data data for the request like date range
 * @param eids list of employee ids from selection
 */
export const bulkRequestTimekeepingSignatures = (
    data: {
        start_date?: string
        end_date?: string
        has_signature: boolean
    },
    eids: number[],
    preFlightErrors: string[]
): Thunk => {
    return dispatch => {
        let errors: string[] = []
        const successes: string[] = []
        let totalResponses = 0
        dispatch(
            setNotificationMessage(
                // eslint-disable-next-line max-len
                "Requesting signatures. Please do not close this window.",
                "info"
            )
        )
        eids.forEach(empId => {
            API.requestSignature({ ...data, eid: empId, return_url: false, is_mobile: true })
                .then(response => {
                    dispatch(requestTimekeepingSignaturesSuccess(response))
                    if ("text_delivered" in response && response?.message) {
                        successes.push(response?.message)
                    }
                    totalResponses++
                })
                .catch(error => {
                    dispatch(requestTimekeepingSignaturesFailed(error))
                    const errorData = error?.response?.data ?? {}
                    if ("text_delivered" in errorData) {
                        errors.push(errorData?.message)
                    }
                    totalResponses++
                })
                .finally(() => {
                    // we want to wait until we have all the requests back - once we do - send a notification
                    if (totalResponses === eids.length) {
                        // combine the request errors with any preflight errors
                        errors = [...errors, ...preFlightErrors]
                        const divider = "\n• "
                        const allErrors = errors.join(divider)
                        if (!errors.length && successes.length) {
                            dispatch(
                                setNotificationMessage("All requests sent successfully", "success", {
                                    autoCloseSeconds: 4,
                                })
                            )
                        } else if (!successes.length && errors.length) {
                            dispatch(setNotificationMessage("All signature requests failed:" + divider + allErrors, "error"))
                        } else {
                            dispatch(
                                setNotificationMessage(
                                    // eslint-disable-next-line max-len
                                    `${successes.length} requests succeeded. The following signature requests failed:` + divider + allErrors,
                                    "warning"
                                )
                            )
                        }
                    }
                })
        })
    }
}

/**
 * @param data data for the request like date range
 * @param target which TKSignTimecardModal instance should handle in-app signing
 */
export const requestTimekeepingSignatures = (
    data: { start_date: string; end_date: string; eid: number; return_url: boolean; is_mobile: boolean; has_signature: boolean },
    target: TKSignTimecardModalTarget = "mainTable"
): Thunk => {
    return dispatch => {
        if (!data?.start_date || !data?.end_date) {
            setNotificationMessage("You must include a start/end date to request a signature", "error", {
                autoCloseSeconds: 4,
            })
            return
        }
        API.requestSignature(data)
            .then(response => {
                dispatch(requestTimekeepingSignaturesSuccess(response))
                if (data.return_url) dispatch(openTKSignTimecardModal(target))
                if ("text_delivered" in response && response?.message) {
                    dispatch(
                        setNotificationMessage(response.message, response.text_delivered ? "success" : "error", {
                            autoCloseSeconds: 4,
                        })
                    )
                }
            })
            .catch(error => {
                dispatch(requestTimekeepingSignaturesFailed(error))
                const errorData = error?.response?.data ?? {}
                if ("text_delivered" in errorData) {
                    dispatch(
                        // We expect text_delivered to be false, but go by the details in the errorData
                        setNotificationMessage(errorData?.message ?? "Error with your request.", errorData?.text_delivered ? "success" : "error", {
                            autoCloseSeconds: 4,
                        })
                    )
                }
            })
    }
}

const SIGNATURE_STATE_RESOURCES = ["timekeepingEntries", "employeeEntries", "ewsStartStopTimes"]

export type SignatureStateUpdates = {
    timekeepingEntries: TimekeepingEntry[]
    employeeEntries: EmployeeFormStore[]
    ewsStartStopTimes: EmployeeWorkShiftStartStopTime[]
}

type SignatureSourceData = {
    timekeepingEntries?: TimekeepingEntry[]
    employeeEntries?: EmployeeFormStore[]
    ewsStartStopTimes?: EmployeeWorkShiftStartStopTime[]
} | null

/**
 * Apply each signature's new state to the loaded rows it covers.
 * @param rows The loaded rows for a single resource
 * @param stateBySignatureId The new state of each signature that changed
 * @returns Copies of only the rows whose signature_state actually changed
 */
const updateSignatureStateAcrossRows = <T extends SignatureRowFields>(rows: T[], stateBySignatureId: Map<number, SignatureState>): T[] =>
    rows.reduce<T[]>((updated, row) => {
        // A null id means an unsigned worker-day, which belongs to no signature group.
        const newState = row.tk_signature_id == null ? undefined : stateBySignatureId.get(row.tk_signature_id)
        if (newState && newState !== row.signature_state) updated.push({ ...row, signature_state: newState })
        return updated
    }, [])

/**
 * Build the source-data updates that bring every loaded worker-day in line with its signature.
 * @param sourceData The currently loaded Weekly View rows
 * @param stateBySignatureId The new state of each signature that changed
 * @returns The changed rows, grouped by resource
 */
const buildSignatureUpdates = (sourceData: SignatureSourceData, stateBySignatureId: Map<number, SignatureState>): SignatureStateUpdates => ({
    timekeepingEntries: updateSignatureStateAcrossRows(sourceData?.timekeepingEntries ?? [], stateBySignatureId),
    employeeEntries: updateSignatureStateAcrossRows(sourceData?.employeeEntries ?? [], stateBySignatureId),
    ewsStartStopTimes: updateSignatureStateAcrossRows(sourceData?.ewsStartStopTimes ?? [], stateBySignatureId),
})

const countUpdatedRows = (updates: SignatureStateUpdates): number => Object.values(updates).reduce((total, rows) => total + rows.length, 0)

/**
 * Find the signatures covering the given worker-days, using the rows already loaded in the grid.
 * @param sourceData The currently loaded Weekly View rows
 * @param ewsIds The EmployeeWorkShift ids to look up
 * @returns The ids of the signatures covering those worker-days
 */
const signatureIdsForEwsIds = (sourceData: SignatureSourceData, ewsIds: number[]): Set<number> => {
    const coveredRows: SignatureRowFields[] = [
        ...(sourceData?.timekeepingEntries ?? []).filter(row => ewsIds.includes(row.employee_work_shift_id)),
        ...(sourceData?.employeeEntries ?? []).filter(row => ewsIds.includes(row.ews_id)),
        ...(sourceData?.ewsStartStopTimes ?? []).filter(row => ewsIds.includes(row.employee_work_shift_id)),
    ]
    return new Set(coveredRows.map(row => row.tk_signature_id).filter((id): id is number => id != null))
}

/**
 * Bring every loaded worker-day in line with the signature states reported by the API.
 *
 * A signature is valid or invalid as a whole, but the API reports a recomputed signature_state
 * only for the rows a request actually touched. Editing one day of a weekly signature therefore
 * leaves the other days it covers holding a stale state until the page is reloaded.
 * @param changedRows Rows returned by the API, carrying a signature id and its current state
 */
export const propagateSignatureStates = (changedRows: SignatureRowFields[]): Thunk => {
    return (dispatch, getState) => {
        if (!getFlagEnabled("WA-9662-sig-reval-ui-followup")) return

        const stateBySignatureId = new Map<number, SignatureState>()
        changedRows.forEach(row => {
            if (row.tk_signature_id != null && row.signature_state) stateBySignatureId.set(row.tk_signature_id, row.signature_state)
        })
        if (!stateBySignatureId.size) return

        const sourceData = sourceDataSelector(getState(), SIGNATURE_STATE_RESOURCES) as SignatureSourceData
        const updates = buildSignatureUpdates(sourceData, stateBySignatureId)
        if (countUpdatedRows(updates)) dispatch(sourceDataUpdated(updates))
    }
}

/**
 * Accept the current state of a worker-day's post-signature edits as benign, re-baselining its
 * signature to the current data so it reads SIGNED again without a full re-sign.
 * @param ewsId The EmployeeWorkShift id whose signature should be rebaselined
 * @param comment Optional note explaining why the edits are benign
 * @returns The updated entries on success (so a caller embedded in its own local grid state, like
 * weekly-tk-detail-table-modal.tsx, can additionally push them through its own updateSourceDataCb)
 * or null on failure.
 */
export const rebaselineSignature = (ewsId: number, comment?: string): Thunk<Promise<SignatureStateUpdates | null>> => {
    return async (dispatch, getState) => {
        try {
            const response = await API.markEditsBenign({ ews_ids: [ewsId], comment: comment?.trim() || null })
            const rebaselinedIds: number[] = response.rebaselined_ews_ids ?? []
            if (!rebaselinedIds.length) {
                dispatch(setNotificationMessage("Something went wrong. Please refresh and try again.", "error", { autoCloseSeconds: 4 }))
                return null
            }

            const sourceData = sourceDataSelector(getState(), SIGNATURE_STATE_RESOURCES) as SignatureSourceData
            let updates: SignatureStateUpdates
            if (getFlagEnabled("WA-9662-sig-reval-ui-followup")) {
                // Accepting covers the signature as a whole, so clear the invalidated state from
                // every loaded day those signatures cover, not just the ids the endpoint echoed.
                const stateBySignatureId = new Map<number, SignatureState>()
                signatureIdsForEwsIds(sourceData, rebaselinedIds).forEach(signatureId => stateBySignatureId.set(signatureId, "SIGNED"))
                updates = buildSignatureUpdates(sourceData, stateBySignatureId)
            } else {
                updates = {
                    timekeepingEntries: (sourceData?.timekeepingEntries ?? [])
                        .filter(entry => rebaselinedIds.includes(entry.employee_work_shift_id))
                        .map(entry => ({ ...entry, signature_state: "SIGNED" as const })),
                    employeeEntries: (sourceData?.employeeEntries ?? []).filter(entry => rebaselinedIds.includes(entry.ews_id)).map(entry => ({ ...entry, signature_state: "SIGNED" as const })),
                    ewsStartStopTimes: (sourceData?.ewsStartStopTimes ?? [])
                        .filter(entry => rebaselinedIds.includes(entry.employee_work_shift_id))
                        .map(entry => ({ ...entry, signature_state: "SIGNED" as const })),
                }
            }
            dispatch(sourceDataUpdated(updates))
            dispatch(setNotificationMessage("Successfully accepted changes. Existing signature was kept.", "success", { autoCloseSeconds: 4 }))
            return updates
        } catch {
            // mark_edits_benign returns no response body on its 403/404 paths, so there is nothing
            // useful to read off error.response.data here.
            dispatch(setNotificationMessage("Something went wrong. Please refresh and try again.", "error", { autoCloseSeconds: 4 }))
            return null
        }
    }
}

// Some helpers for the list view buttons

/**
 * Gets tooltip for cell actions for in-app signatures
 * @param context ag-grid context with information about selected
 * @returns Relevant tooltip based on settings. May be undefined if no conditions for tooltip met
 */
export const getSignatureTooltip = (context: tContext): string | undefined => {
    const isValidSelection = singleEmployeeIsSelected(context)
    if (!isValidSelection) return "Select an employee cell with timekeeping data to sign for a timecard"
    const { selectedCellData, referenceableData } = context
    const { timekeepingEntries, employeeEntries } = selectedCellData ?? {}
    // if it's a shift extra only timecard, check if shift extras are configured to be displayed on signatures
    if (
        !timekeepingEntries?.length &&
        employeeEntries?.length &&
        !employeeEntries?.filter(e => {
            const relatedSchema = referenceableData.employeeSchemas[e.schema]
            if (!relatedSchema) return false
            return e.display_shift_extras && relatedSchema.include_with_signatures
        }).length
    )
        return "Selected shift extras are not configured to be displayed on the signature"
    return undefined
}

/**
 * Gets all employee ids for employees who have text alerts on, and have not opted out of texting
 * @param entriesToCheck list of timekeeping/shift extra entries from selected cell
 * @param employees Mapping of employee ids and employee objects
 * @param existingErrors Optional. Can return preflight error messages for employees that can't be texted
 * @returns List if employee ids that have text-to-sign activated
 */
export const getTextToSignEmployeeIds = (entriesToCheck: Array<TimekeepingEntry | EmployeeFormStore>, employees: Record<number, iEmployee>, existingErrors?: string[]): number[] => {
    const errors = existingErrors ?? []
    const uniqueEmployeeIds = getUniqueEmployeeIds(entriesToCheck)
    return uniqueEmployeeIds.filter(empId => {
        const empRef = employees?.[empId]
        if (!empRef) return false
        let canSign = true
        if (!empRef.phone) {
            canSign = false
            errors.push(`${empRef.first_name} ${empRef.last_name} does not have a phone number registered with Rhumbix.`)
        } else if (empRef.text_opted_out_on) {
            canSign = false
            errors.push(`${empRef.first_name} ${empRef.last_name} has opted out of texts from Rhumbix.`)
        } else if (!empRef.text_alerts_ok) {
            canSign = false
            errors.push(`${empRef.first_name} ${empRef.last_name} has not signed up for texts from Rhumbix.`)
        }
        return canSign
    })
}

/**
 * Gets tooltip for cell actions for requesting text-to-sign signatures
 * @param context ag-grid context with information about selected
 * @returns Relevant tooltip based on settings. May be undefined if no conditions for tooltip met
 */
export const getSignatureRequestTooltip = (context: tContext): string | undefined => {
    const { selectedCellData, referenceableData } = context
    const { employees } = referenceableData ?? {}
    const { timekeepingEntries } = selectedCellData ?? {}
    if (isEmpty(employees) || !timekeepingEntries) return "Select an employee cell with timekeeping data to sign for a timecard"
    const eligibleEmps = getTextToSignEmployeeIds(timekeepingEntries, employees)
    if (!eligibleEmps?.length) {
        return "The selected employee(s) do not have text-to-sign configured"
    }
}

/**
 * Returns list of all employee ids in list of timkeeping/shift extra entries
 * @param entriesToCheck list of timekeeping entries from selected cell
 * @returns list of employee ids
 */
export const getUniqueEmployeeIds = (entriesToCheck: Array<TimekeepingEntry | EmployeeFormStore>): number[] => {
    const selectedEmployeeIds = entriesToCheck.map(entry => entry.employee)
    return [...new Set(selectedEmployeeIds)]
}

/**
 * Looked at timekeeping info selected and returns if it is a single employee selection
 * @param context ag-grid context with information about selected
 * @returns boolean - true is only one employee in the selection
 */
export const singleEmployeeIsSelected = (context: tContext): boolean => {
    const { selectedCellData } = context
    const { timekeepingEntries, employeeEntries } = selectedCellData ?? {}
    if (!timekeepingEntries && !employeeEntries) return false
    const allEntries = [...timekeepingEntries, ...employeeEntries]
    const selectedEmployeeIds = getUniqueEmployeeIds(allEntries)
    // if we have more than one employee - we can't sign
    if (selectedEmployeeIds?.length === 1) return true
    return false
}
